export const TRANSFER_REASON = {
  RechargeMemberCredits: 1,
  ReclaimMemberCredits: 2,
  RemoveMemberResource: 3,
  TransferCreditsBetweenScopes: 4,
} as const

export type BillingTransferInfo = {
  fromUser?: number
  toUser?: number
  fromUserName?: string
  toUserName?: string
  reason?: number
}

export function getRawTransferDisplayName(name?: string, fallback?: string) {
  const normalizedName = name?.trim()
  if (normalizedName) return normalizedName

  return fallback ?? '-'
}

export function normalizeTransferDisplayName(name?: string, fallback?: string) {
  const normalizedName = getRawTransferDisplayName(name)
  if (normalizedName && normalizedName !== '-') {
    const at = normalizedName.indexOf('@')
    return at > 0 ? normalizedName.slice(0, at) : normalizedName
  }

  return fallback ?? '-'
}

export function getTransferParticipantNames(transferInfo: BillingTransferInfo) {
  const { fromUser, toUser } = transferInfo

  return {
    fromUserName: normalizeTransferDisplayName(
      transferInfo.fromUserName,
      typeof fromUser === 'number' ? String(fromUser) : '-',
    ),
    toUserName: normalizeTransferDisplayName(
      transferInfo.toUserName,
      typeof toUser === 'number' ? String(toUser) : '-',
    ),
  }
}

export function getTransferParticipantRawNames(transferInfo: BillingTransferInfo) {
  const { fromUser, toUser } = transferInfo

  return {
    fromUserName: getRawTransferDisplayName(
      transferInfo.fromUserName,
      typeof fromUser === 'number' ? String(fromUser) : '-',
    ),
    toUserName: getRawTransferDisplayName(
      transferInfo.toUserName,
      typeof toUser === 'number' ? String(toUser) : '-',
    ),
  }
}

export function getPersonalTransferTypeStr({
  transferInfo,
  billUserId,
  t,
}: {
  transferInfo?: BillingTransferInfo | null
  billUserId?: number
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  if (!transferInfo) return null

  const { fromUser: fromUserId, toUser: toUserId, reason } = transferInfo
  const { fromUserName, toUserName } = getTransferParticipantNames(transferInfo)
  const { fromUserName: fromUserNameRaw, toUserName: toUserNameRaw } =
    getTransferParticipantRawNames(transferInfo)

  const ownerIsFromUser = typeof billUserId === 'number' && billUserId === fromUserId
  const ownerIsToUser = typeof billUserId === 'number' && billUserId === toUserId

  switch (reason) {
    case TRANSFER_REASON.RechargeMemberCredits:
      if (ownerIsToUser) {
        return t('teamBilling.transfer.memberRecharge', {
          name: fromUserName,
          defaultValue: `Credit Recharge: ${fromUserName}`,
        })
      }
      if (ownerIsFromUser) {
        return t('teamBilling.transfer.adminRechargedTo', {
          name: toUserName,
          defaultValue: `Credit Recharged to ${toUserName}`,
        })
      }
      return null

    case TRANSFER_REASON.ReclaimMemberCredits:
      if (ownerIsFromUser) {
        return t('teamBilling.transfer.memberReclaim', {
          name: toUserName,
          defaultValue: `Credit Reclaim: ${toUserName}`,
        })
      }
      if (ownerIsToUser) {
        return t('teamBilling.transfer.adminReclaimedFrom', {
          name: fromUserName,
          defaultValue: `Credit Reclaimed from ${fromUserName}`,
        })
      }
      return null

    case TRANSFER_REASON.RemoveMemberResource:
      if (ownerIsFromUser) {
        return t('teamBilling.transfer.removalReclaimSelf', {
          defaultValue: 'Credit Reclaim (member removed)',
        })
      }
      if (ownerIsToUser) {
        return t('teamBilling.transfer.removalReclaimFrom', {
          name: fromUserName,
          defaultValue: `Credit Reclaimed from removed member ${fromUserName}`,
        })
      }
      return null

    case TRANSFER_REASON.TransferCreditsBetweenScopes:
      if (ownerIsFromUser) {
        return t('teamBilling.transfer.scopeTransferToTeamNamed', {
          name: toUserNameRaw,
          defaultValue: `Transfer to team account: ${toUserNameRaw}`,
        })
      }
      if (ownerIsToUser) {
        return t('teamBilling.transfer.scopeTransferFromSelfNamed', {
          name: fromUserNameRaw,
          defaultValue: `Received from personal account: ${fromUserNameRaw}`,
        })
      }
      return null

    default:
      return null
  }
}
